'use client';

import { FunctionComponent, useEffect } from 'react';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import { datadogRum } from '@datadog/browser-rum';
import { useLocale } from '@/react/hooks/useLocale';
import { removeNullish } from '@/utils/object-utils';

/**
 * A component for holding meta data added to the head.
 */
export const Datadog: FunctionComponent = () => {
  const { language, country } = useLocale();
  const dataDogConfig = ConfigurationService.getConfig(
    'datadog',
    language,
    country
  );
  const isDataDogEnabled = dataDogConfig.getSetting('enabled').value;

  useEffect(() => {
    // Check if running on localhost.
    //
    // WARNING: Do not use `EnvironmentService.isLocalDevelopment` here because
    // it will cause a static optimization error.
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');

    // Disable Datadog for development environment and localhost deployments
    if (isDataDogEnabled && !(process.env.NEXT_PUBLIC_APP_ENV === "dev") && !isLocalhost) {
      const clientToken = dataDogConfig.getSetting('clientToken').value;
      const applicationId = dataDogConfig.getSetting('applicationId').value;
      const sessionSampleRate =
        dataDogConfig.getSetting('sessionSampleRate').value;
      const sessionReplaySampleRate = dataDogConfig.getSetting(
        'sessionReplaySampleRate'
      ).value;
      const env = dataDogConfig.getSetting('env').value;
      const service = dataDogConfig.getSetting('service').value;
      const trackUserInteractions = dataDogConfig.getSetting(
        'trackUserInteractions'
      ).value;
      const trackResources = dataDogConfig.getSetting('trackResources').value;
      const trackLongTasks = dataDogConfig.getSetting('trackLongTasks').value;
      const defaultPrivacyLevel = dataDogConfig.getSetting(
        'defaultPrivacyLevel'
      ).value;
      const silentMultipleInit =
        dataDogConfig.getSetting('silentMultipleInit').value;
      const useSecureSessionCookie = dataDogConfig.getSetting(
        'useSecureSessionCookie'
      ).value;
      const version = dataDogConfig.getSetting('version').value;
      const traceSampleRate = dataDogConfig.getSetting('traceSampleRate').value;
      const excludedActivityUrlsPattern = dataDogConfig.getSetting(
        'excludedActivityUrls'
      ).value;
      const excludedActivityUrls = [new RegExp(excludedActivityUrlsPattern)];

      // Generate allowedTracingUrls dynamically based on current deployment URL
      const { baseURL } = EnvironmentService;

      const allowedTracingUrls = [
        {
          match: baseURL,
          propagatorTypes: dataDogConfig.getSetting('propagatorTypes').value
        }
      ];

      datadogRum.init(
        removeNullish({
          clientToken,
          applicationId,
          sessionSampleRate,
          sessionReplaySampleRate,
          env,
          service,
          trackUserInteractions,
          trackResources,
          trackLongTasks,
          defaultPrivacyLevel,
          silentMultipleInit,
          useSecureSessionCookie,
          version,
          allowedTracingUrls,
          traceSampleRate,
          excludedActivityUrls
        })
      );
    }

    // Previous versions would start a session replay recording here. This is
    // not needed for v5+.
    //
    // See https://docs.datadoghq.com/real_user_monitoring/browser/setup/client/?tab=rum
  }, []);

  return null;
};
